import React, { useEffect, useState } from "react";
import { fmtAbbr } from "../../components/terminal/format.js";
import { terminalAPI } from "../../apis/TerminalAPI.jsx";

/* Pending Approvals + Active Breaches drill-down panes. Triggered
   by clicking the matching status-spine cells in the cockpit. Each
   pane is a full-screen overlay (Bloomberg style — no animation
   beyond a 120ms opacity fade). Data shapes mirror §D.10. */

const MOCK_APPROVALS = [
  { id:"APR-2401", kind:"yield_deploy", subject_label:"Deploy 500K USD → US T-Bill 13w",   amount_usd_eq:"500000",   requested_by:"felix@blox.io",   requested_at_iso:"10:18", approvers_required:2, approvers_so_far:[{ user_email:"olivia@blox.io" }] },
  { id:"APR-2402", kind:"move",         subject_label:"Move 1M USD · Op → Reserves",        amount_usd_eq:"1000000",  requested_by:"maya@blox.io",    requested_at_iso:"10:24", approvers_required:2, approvers_so_far:[] },
  { id:"APR-2403", kind:"borrow",       subject_label:"Borrow 200K USDC against 5 BTC",     amount_usd_eq:"200000",   requested_by:"felix@blox.io",   requested_at_iso:"09:51", approvers_required:2, approvers_so_far:[{ user_email:"olivia@blox.io" }] },
  { id:"APR-2404", kind:"sweep",        subject_label:"Manual sweep · all entities",        amount_usd_eq:"554300",   requested_by:"olivia@blox.io",  requested_at_iso:"09:42", approvers_required:1, approvers_so_far:[] },
  { id:"APR-2405", kind:"forward",      subject_label:"Forward hedge 2M EUR · 30d",          amount_usd_eq:"2180000",  requested_by:"maya@blox.io",    requested_at_iso:"08:58", approvers_required:2, approvers_so_far:[{ user_email:"felix@blox.io" }] },
  { id:"APR-2406", kind:"yield_redeem", subject_label:"Redeem 100K USDC · Maple Cash",       amount_usd_eq:"100000",   requested_by:"maya@blox.io",    requested_at_iso:"08:14", approvers_required:1, approvers_so_far:[] },
  { id:"APR-2407", kind:"lend",         subject_label:"Lend 500K USDC · 30d",                amount_usd_eq:"500000",   requested_by:"felix@blox.io",   requested_at_iso:"07:42", approvers_required:2, approvers_so_far:[] },
];

const MOCK_BREACHES = [
  { id:"BR-1", kind:"ltv_threshold", severity:"high", message:"L-2401 · LTV approaching 65% (BTC -3.2% 24h)", subject_id:"L-2401", subject_kind:"loan",  first_seen_iso:"10:31", suggested_action:{ id:"addcoll", label:"Add collateral" } },
  { id:"BR-2", kind:"min_balance",    severity:"med",  message:"Blox EU SARL operating below min ($412k → $500k)", subject_id:"E-002", subject_kind:"entity", first_seen_iso:"09:48", suggested_action:{ id:"sweep", label:"Sweep now" } },
];

const KIND_ACCENT = {
  yield_deploy:"#A78BFA", yield_redeem:"#A78BFA",
  move:"#4A8CFF", sweep:"#4A8CFF",
  borrow:"#FF5F6B", lend:"#FF5F6B", repay:"#FF5F6B",
  forward:"#06B6D4", spot:"#06B6D4",
};

function OverlayShell({ title, count, onClose, children }) {
  return (
    <div
      onClick={onClose}
      style={{
        position:"fixed", inset:0, background:"rgba(8,11,16,0.78)",
        backdropFilter:"blur(8px)", WebkitBackdropFilter:"blur(8px)",
        zIndex:9998, display:"flex", alignItems:"flex-start", justifyContent:"center", paddingTop:80,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width:780, maxWidth:"94vw", maxHeight:"80vh", overflow:"auto",
          background:"var(--term-bg-pane)", border:"1px solid var(--term-line)",
          boxShadow:"0 24px 60px rgba(0,0,0,0.6)",
        }}
      >
        <div style={{ padding:"14px 18px", borderBottom:"1px solid var(--term-line)", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            <span style={{ fontSize:11, letterSpacing:"0.16em", color:"var(--term-ink-1)", fontWeight:600, textTransform:"uppercase" }}>{title}</span>
            <span className="mono" style={{ fontSize:10, color:"var(--term-ink-3)", letterSpacing:"0.10em" }}>{count} items</span>
          </div>
          <button onClick={onClose} style={{ background:"transparent", border:"1px solid var(--term-line)", color:"var(--term-ink-2)", padding:"4px 9px", fontSize:10, fontFamily:"inherit", cursor:"pointer" }}>ESC</button>
        </div>
        {children}
      </div>
    </div>
  );
}

/* useLiveOrMock — fetches live data via the supplied terminalAPI
   method and falls back to the local MOCK_* if the endpoint is
   unreachable or returns nothing. Per the project's graceful-
   degradation rule, list read endpoints never break the UI. */
function useLiveOrMock(token, fetcher, mock, listKey = "items") {
  const [data, setData] = useState(mock);
  useEffect(() => {
    if (!token) { setData(mock); return; }
    let cancelled = false;
    fetcher(token).then((r) => {
      if (cancelled) return;
      const list = Array.isArray(r?.data?.[listKey]) ? r.data[listKey]
                : Array.isArray(r?.data) ? r.data
                : null;
      setData(list && list.length ? list : mock);
    }).catch(() => { if (!cancelled) setData(mock); });
    return () => { cancelled = true; };
  }, [token, fetcher, mock, listKey]);
  return data;
}

export function ApprovalsDrawer({ token, currentUserEmail, approvals: override, onApprove, onReject, onClose }) {
  const fetched = useLiveOrMock(
    token,
    (t) => terminalAPI.listApprovals(t, "pending"),
    MOCK_APPROVALS,
  );
  const approvals = override || fetched;
  const [rowError, setRowError] = useState({});

  /* §J.4 self-approval guard. BE rejects with `cannot_self_approve`
     server-side, but the FE pre-empts the attempt for a cleaner UX:
     the Approve button is disabled when the current user submitted
     the request, with a tooltip explaining why. */
  const isSelfSubmitted = (a) =>
    currentUserEmail && a.requested_by &&
    a.requested_by.toLowerCase() === currentUserEmail.toLowerCase();

  const handleApprove = (a) => {
    if (isSelfSubmitted(a)) {
      setRowError((m) => ({ ...m, [a.id]: "You can't approve your own submission." }));
      return;
    }
    setRowError((m) => ({ ...m, [a.id]: null }));
    onApprove?.(a);
  };

  return (
    <OverlayShell title="Pending approvals" count={approvals.length} onClose={onClose}>
      <div style={{ display:"flex", flexDirection:"column" }}>
        <div style={{ display:"grid", gridTemplateColumns:"90px 1fr 100px 110px 120px 140px", gap:12, padding:"8px 18px", background:"var(--term-bg-pane-2)", borderBottom:"1px solid var(--term-line)", fontSize:9, letterSpacing:"0.16em", color:"var(--term-ink-3)", fontWeight:600, textTransform:"uppercase" }}>
          <span>KIND</span><span>SUBJECT</span><span style={{ textAlign:"right" }}>AMOUNT</span><span>REQUESTED</span><span>APPROVERS</span><span style={{ textAlign:"right" }}>ACTIONS</span>
        </div>
        {approvals.map((a) => {
          const accent = KIND_ACCENT[a.kind] || "var(--term-ink-2)";
          const remaining = a.approvers_required - (a.approvers_so_far?.length || 0);
          const selfBlocked = isSelfSubmitted(a);
          const err = rowError[a.id];
          return (
            <div key={a.id} style={{ display:"grid", gridTemplateColumns:"90px 1fr 100px 110px 120px 140px", gap:12, alignItems:"center", padding:"10px 18px", borderBottom:"1px solid var(--term-line-dim)", fontSize:11 }}>
              <span style={{ display:"inline-flex", alignItems:"center", gap:6, fontSize:9.5, letterSpacing:"0.10em", color:accent, textTransform:"uppercase", fontWeight:600 }}>
                <span style={{ width:5, height:5, background:accent }} />
                {a.kind.replace("_"," ")}
              </span>
              <span style={{ color:"var(--term-ink-1)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                {a.subject_label}
                <span className="mono" style={{ fontSize:9.5, color:"var(--term-ink-3)", marginLeft:8 }}>{a.id}</span>
                {err && (
                  <span style={{ display:"block", fontSize:9.5, color:"var(--term-red)", marginTop:2, letterSpacing:"0.04em" }}>{err}</span>
                )}
              </span>
              <span className="mono" style={{ textAlign:"right", color:"var(--term-ink-1)" }}>{fmtAbbr(a.amount_usd_eq)}</span>
              <span className="mono" style={{ fontSize:10, color:"var(--term-ink-3)" }}>
                {a.requested_at_iso}
                <span style={{ display:"block", color:"var(--term-ink-3)" }}>{a.requested_by}</span>
              </span>
              <span className="mono" style={{ fontSize:10, color: remaining === 0 ? "var(--term-green)" : "var(--term-amber)" }}>
                {a.approvers_so_far?.length || 0}/{a.approvers_required} · need {remaining}
              </span>
              <span style={{ display:"flex", gap:6, justifyContent:"flex-end" }}>
                <button
                  onClick={() => onReject?.(a)}
                  className="term-util-btn"
                  style={{ color:"var(--term-red)", borderColor:"var(--term-red)" }}
                >Reject</button>
                <button
                  onClick={() => handleApprove(a)}
                  disabled={selfBlocked}
                  title={selfBlocked ? "You can't approve your own submission" : "Approve"}
                  style={{
                    height:24, padding:"0 10px", fontSize:10.5, letterSpacing:"0.06em",
                    fontFamily:"inherit",
                    background: selfBlocked ? "var(--term-line)" : "var(--term-green)",
                    color: selfBlocked ? "var(--term-ink-3)" : "#000",
                    border: selfBlocked ? "1px solid var(--term-line)" : "1px solid var(--term-green)",
                    cursor: selfBlocked ? "not-allowed" : "pointer",
                    fontWeight:600,
                  }}
                >Approve</button>
              </span>
            </div>
          );
        })}
        {approvals.length === 0 && (
          <div className="mono" style={{ padding:"40px 18px", textAlign:"center", color:"var(--term-ink-3)", fontSize:11.5 }}>
            no approvals pending · all clear
          </div>
        )}
      </div>
    </OverlayShell>
  );
}

export function BreachesDrawer({ token, breaches: override, onAck, onActOn, onClose }) {
  const fetched = useLiveOrMock(
    token,
    (t) => terminalAPI.listBreaches(t, "active"),
    MOCK_BREACHES,
  );
  const breaches = override || fetched;
  const sevColor = { high:"var(--term-red)", med:"var(--term-amber)", low:"#4A8CFF" };
  return (
    <OverlayShell title="Active breaches" count={breaches.length} onClose={onClose}>
      <div style={{ display:"flex", flexDirection:"column" }}>
        <div style={{ display:"grid", gridTemplateColumns:"70px 90px 1fr 90px 230px", gap:12, padding:"8px 18px", background:"var(--term-bg-pane-2)", borderBottom:"1px solid var(--term-line)", fontSize:9, letterSpacing:"0.16em", color:"var(--term-ink-3)", fontWeight:600, textTransform:"uppercase" }}>
          <span>SEV</span><span>KIND</span><span>MESSAGE</span><span>SINCE</span><span style={{ textAlign:"right" }}>SUGGESTED ACTION</span>
        </div>
        {breaches.map((b) => (
          <div key={b.id} style={{ display:"grid", gridTemplateColumns:"70px 90px 1fr 90px 230px", gap:12, alignItems:"center", padding:"12px 18px", borderBottom:"1px solid var(--term-line-dim)", fontSize:11 }}>
            <span style={{ display:"inline-flex", alignItems:"center", gap:6, fontSize:9.5, letterSpacing:"0.14em", color:sevColor[b.severity], fontWeight:600 }}>
              <span style={{ width:6, height:6, background:sevColor[b.severity], boxShadow:`0 0 8px ${sevColor[b.severity]}` }} />
              {b.severity.toUpperCase()}
            </span>
            <span className="mono" style={{ fontSize:9.5, color:"var(--term-ink-3)", textTransform:"uppercase", letterSpacing:"0.08em" }}>{b.kind.replace("_"," ")}</span>
            <span style={{ color:"var(--term-ink-1)" }}>
              {b.message}
              <span className="mono" style={{ fontSize:9.5, color:"var(--term-ink-3)", marginLeft:8 }}>{b.id} · {b.subject_id}</span>
            </span>
            <span className="mono" style={{ fontSize:10, color:"var(--term-ink-3)" }}>{b.first_seen_iso}</span>
            <span style={{ display:"flex", gap:6, justifyContent:"flex-end" }}>
              <button onClick={() => onAck?.(b)} className="term-util-btn">Acknowledge</button>
              <button onClick={() => onActOn?.(b)}
                style={{ height:24, padding:"0 10px", fontSize:10.5, letterSpacing:"0.06em", fontFamily:"inherit", background:sevColor[b.severity], color:"#000", border:`1px solid ${sevColor[b.severity]}`, cursor:"pointer", fontWeight:600 }}>
                {b.suggested_action.label} →
              </button>
            </span>
          </div>
        ))}
        {breaches.length === 0 && (
          <div className="mono" style={{ padding:"40px 18px", textAlign:"center", color:"var(--term-ink-3)", fontSize:11.5 }}>
            no active breaches · all systems green
          </div>
        )}
      </div>
    </OverlayShell>
  );
}
